/**
 * SearchFilters Component
 * Search and filter controls for slip management
 */

"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { Search, ChevronDown } from "lucide-react"
import Image from "@/src/components/Image"
import { BankAccount } from "@/src/types/slip"

const STATUS_OPTIONS = [
  { value: '',          label: 'สถานะทั้งหมด' },
  { value: 'verified',  label: 'ตรวจสอบแล้ว' },
  { value: 'failed',    label: 'ไม่สำเร็จ' },
  { value: 'duplicate', label: 'สลิปซ้ำ' },
  { value: 'pending',   label: 'รอดำเนินการ' },
  { value: 'cached',    label: 'จากแคช' },
]

// API channel is excluded from this view (transactions from external API integrations
// are managed separately — see the API usage/reports section)
const CHANNEL_OPTIONS = [
  { value: '',       label: 'ช่องทางทั้งหมด' },
  { value: 'MANUAL', label: 'เว็บ / Manual' },
  { value: 'LINE',   label: 'LINE' },
]

interface SearchFiltersProps {
  accounts: BankAccount[]
  onSearch: (searchTerm: string) => void
  onAccountFilter: (accountId: string | undefined) => void
  onStatusFilter: (status: string) => void
  onChannelFilter: (channel: string) => void
}

export function SearchFilters({
  accounts,
  onSearch,
  onAccountFilter,
  onStatusFilter,
  onChannelFilter,
}: SearchFiltersProps) {
  const [searchTerm, setSearchTerm]         = useState("")
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>()
  const [selectedStatus, setSelectedStatus]   = useState("")
  const [selectedChannel, setSelectedChannel] = useState("")

  const handleSearch = () => onSearch(searchTerm)

  const handleAccountSelect = (accountId: string | undefined) => {
    setSelectedAccount(accountId)
    onAccountFilter(accountId)
  }

  const handleStatusSelect = (value: string) => {
    setSelectedStatus(value)
    onStatusFilter(value)
  }

  const handleChannelSelect = (value: string) => {
    setSelectedChannel(value)
    onChannelFilter(value)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch()
  }

  const getSelectedAccountLabel = () => {
    if (!selectedAccount) return "บัญชีทั้งหมด"
    const account = accounts.find(acc => acc.id === selectedAccount)
    return account ? `${account.bank_code} - ${account.account_number}` : "บัญชีทั้งหมด"
  }

  const selectedStatusLabel  = STATUS_OPTIONS.find(o => o.value === selectedStatus)?.label  || 'สถานะทั้งหมด'
  const selectedChannelLabel = CHANNEL_OPTIONS.find(o => o.value === selectedChannel)?.label || 'ช่องทางทั้งหมด'

  return (
    <div className="flex flex-col gap-3">
      {/* Row 1: Search bar */}
      <div className="flex gap-2 flex-1">
        <div className="relative flex-1 max-w-2xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder="ชื่อผู้รับ, ชื่อผู้ส่ง, เลขอ้างอิง, เลขบัญชี"
            className="pl-10 h-11"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>
        <Button
          variant="secondary"
          className="bg-gray-400 hover:bg-gray-500 text-white px-6 h-11"
          onClick={handleSearch}
        >
          ค้นหา
        </Button>
      </div>

      {/* Row 2: Dropdowns */}
      <div className="flex flex-wrap gap-2">
        {/* Account filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 h-10 px-4 bg-transparent">
              <Image src="/icons/icon-wallet.svg" alt="Wallet" width={20} height={18} className="flex-shrink-0" />
              <span>{getSelectedAccountLabel()}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56" style={{ backgroundColor: 'white' }}>
            <DropdownMenuItem onClick={() => handleAccountSelect(undefined)}>
              บัญชีทั้งหมด
            </DropdownMenuItem>
            {accounts.map((account) => (
              <DropdownMenuItem key={account.id} onClick={() => handleAccountSelect(account.id)}>
                {account.bank_code} - {account.account_number}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Status filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 h-10 px-4 bg-transparent">
              <span>{selectedStatusLabel}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48" style={{ backgroundColor: 'white' }}>
            {STATUS_OPTIONS.map((opt) => (
              <DropdownMenuItem key={opt.value} onClick={() => handleStatusSelect(opt.value)}>
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Channel filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 h-10 px-4 bg-transparent">
              <span>{selectedChannelLabel}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48" style={{ backgroundColor: 'white' }}>
            {CHANNEL_OPTIONS.map((opt) => (
              <DropdownMenuItem key={opt.value} onClick={() => handleChannelSelect(opt.value)}>
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
